const VALIDATABLE_INPUT_TYPES = new Set([
  'dlsInput',
  'input',
  'textArea',
  'radioButton',
  'checkbox',
  'currencyInput',
  'currency',
  'dlsSelect',
  'select',
]);

const asArray = (value) => (Array.isArray(value) ? value : []);

const getNestedElements = (element) => [
  ...asArray(element?.childElements),
  ...asArray(element?.elements),
  ...asArray(element?.elementContainer),
];

const isTableRequirementMet = (tableConfig, formSubmissionPayload) => {
  // If table has no requirement condition, it's always required (if isTableLengthRequired is true)
  if (!tableConfig?.requirementCondition) {
    return true;
  }

  // Check requirementCondition.selectKey and valueToCompare
  const { selectKey, valueToCompare } = tableConfig.requirementCondition;
  if (selectKey && valueToCompare !== undefined) {
    const fieldValue = formSubmissionPayload?.[selectKey];
    return fieldValue === valueToCompare;
  }

  return true;
};

const isMissingValue = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue === '' || trimmedValue === 'Off' || trimmedValue === 'No';
  }
  if (typeof value === 'boolean') return value !== true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const getSectionLabel = (node) => {
  if (!node) return null;
  if (node.section) return node.section;
  if (Array.isArray(node.childElements)) {
    const sectionChild = node.childElements.find((child) => child && child.section);
    if (sectionChild) return sectionChild.section;
  }
  return null;
};

// Keys that represent transient UI navigation state (e.g. "which sub-tab/step is currently
// being viewed") rather than a real business condition. A field gated purely on one of these
// must never be treated as "permanently hidden" for validation purposes — by the time the user
// reaches the final review page, the nav key has moved on to whatever step they last visited,
// which would otherwise make earlier sub-tabs' required fields silently skip validation.
const NAVIGATION_ONLY_KEYS = new Set(['navDefaultSection']);

const normalizePayloadKey = (key) => (
  key.startsWith('formSubmissionPayload.') ? key.slice('formSubmissionPayload.'.length) : key
);

// Lookup a value from formSubmissionPayload supporting nested dot-notation paths
// e.g. fieldId = "formSubmissionPayload.person.name" or "person.name"
const getPayloadValue = (fieldId, formSubmissionPayload) => {
  if (!fieldId || !formSubmissionPayload) return undefined;

  const normalizedPath = fieldId.startsWith('formSubmissionPayload.')
    ? fieldId.slice('formSubmissionPayload.'.length)
    : fieldId;

  return normalizedPath.split('.').reduce((acc, part) => {
    if (acc === null || acc === undefined || typeof acc !== 'object' || !(part in acc)) {
      return undefined;
    }
    return acc[part];
  }, formSubmissionPayload);
};

// Core display-condition evaluator. Handles two formats found in the form JSON:
//   Format 1: { propertyKey, mapCondition:
//              { condition, valueToCompare, truthyAction, falsyAction } }
//   Format 2: { multiKeyValueMappings: { key: [values] }, mapCondition: "AND",
//              truthyAction, falsyAction }
// Array expected values use Array.includes() so [3.1] matches the number 3.1.
// When the element has removeIdsFromPayloadWhen, only the listed keys are checked
// in multiKeyValueMappings — navigation conditions (navDefaultSection etc.) are ignored.
// Checks record data first (for expandable-table fields), then formSubmissionPayload.
// eslint-disable-next-line complexity -- handles two display-condition formats with multiple early-exit branches
const evaluateIsHidden = (element, formSubmissionPayload, record = null) => {
  const fieldDynamicDisplay = element?.fieldDynamicDisplay;
  if (!fieldDynamicDisplay || typeof fieldDynamicDisplay !== 'object') return false;
  if (Object.keys(fieldDynamicDisplay).length === 0) return false;

  const getValue = (key) => {
    if (record) {
      const normalizedKey = key.startsWith('formSubmissionPayload.')
        ? key.slice('formSubmissionPayload.'.length)
        : key;
      if (Object.prototype.hasOwnProperty.call(record, normalizedKey)) {
        return record[normalizedKey];
      }
    }
    return getPayloadValue(key, formSubmissionPayload);
  };

  // Format 1: propertyKey + mapCondition object
  if (fieldDynamicDisplay.propertyKey
      && fieldDynamicDisplay.mapCondition
      && typeof fieldDynamicDisplay.mapCondition === 'object') {
    // A condition gated solely on a navigation-only key (e.g. "show while on sub-tab 3.1")
    // is never treated as hiding — it reflects UI focus, not whether the field is required.
    if (NAVIGATION_ONLY_KEYS.has(normalizePayloadKey(fieldDynamicDisplay.propertyKey))) {
      return false;
    }
    const mc = fieldDynamicDisplay.mapCondition;
    const current = getValue(fieldDynamicDisplay.propertyKey);
    let conditionMet;
    if (mc.condition === 'equalTo') conditionMet = current === mc.valueToCompare;
    else if (mc.condition === 'notEqualTo') conditionMet = current !== mc.valueToCompare;
    // 'valuePresent' has no valueToCompare — matches conditionEvaluator.js's runtime semantics
    // (truthy and non-empty-string), not a strict equality check against undefined.
    else if (mc.condition === 'valuePresent') conditionMet = Boolean(current) && current !== '';
    else conditionMet = current === mc.valueToCompare;
    const ta = mc.truthyAction || fieldDynamicDisplay.truthyAction || 'show';
    const fa = mc.falsyAction || fieldDynamicDisplay.falsyAction || 'hide';
    return conditionMet ? ta === 'hide' : fa === 'hide';
  }

  // Format 2: multiKeyValueMappings + mapCondition "AND"
  if (fieldDynamicDisplay.multiKeyValueMappings
      && fieldDynamicDisplay.mapCondition === 'AND') {
    let entries = Object.entries(fieldDynamicDisplay.multiKeyValueMappings);
    if (entries.length === 0) return false; // empty mappings → always visible

    // If removeIdsFromPayloadWhen is present on the element, only evaluate the conditions
    // for those specific keys. This ignores navigation-only conditions (e.g. navDefaultSection)
    // that are irrelevant for field-requirement validation.
    const removeWhenKeys = asArray(element?.removeIdsFromPayloadWhen);
    if (removeWhenKeys.length > 0) {
      const removeWhenSet = new Set(removeWhenKeys);
      const filtered = entries.filter(([key]) => {
        const normalizedKey = key.startsWith('formSubmissionPayload.')
          ? key.slice('formSubmissionPayload.'.length)
          : key;
        return removeWhenSet.has(normalizedKey);
      });
      // If none of the removeIdsFromPayloadWhen keys appear in mappings, fall back to all entries
      if (filtered.length > 0) entries = filtered;
    }

    const allMet = entries.every(([key, expected]) => {
      const current = getValue(key);
      return Array.isArray(expected) ? expected.includes(current) : current === expected;
    });
    const ta = fieldDynamicDisplay.truthyAction || 'show';
    const fa = fieldDynamicDisplay.falsyAction || 'hide';
    return allMet ? ta === 'hide' : fa === 'hide';
  }

  return false;
};

// Build a flat map of fieldId → element for the given elements tree
const buildElementByFieldId = (elements) => {
  const map = {};
  const traverse = (element) => {
    if (!element || typeof element !== 'object') return;
    if (element.fieldId) map[element.fieldId] = element;
    getNestedElements(element).forEach(traverse);
  };
  asArray(elements).forEach(traverse);
  return map;
};

const extractSummarySections = (formElements = []) => {
  if (!Array.isArray(formElements)) return [];

  const summaryPages = formElements.filter(
    (node) => node && typeof node === 'object' && node.summary === true
  );
  const sections = [];

  const processNodeArray = (nodes, pageNo = null) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return;

    const hasSectionMarkers = nodes.some((node) => getSectionLabel(node) !== null);

    if (hasSectionMarkers) {
      let currentSection = null;
      nodes.forEach((node) => {
        if (!node) return;
        const sectionLabel = getSectionLabel(node);
        if (sectionLabel) {
          const existingSection = sections.find((section) => section.id === sectionLabel);
          if (!existingSection) {
            currentSection = {
              id: sectionLabel,
              label: sectionLabel,
              pageNo,
              elements: [],
            };
            sections.push(currentSection);
          } else {
            currentSection = existingSection;
          }
        }

        if (currentSection) {
          currentSection.elements.push(node);
        }
      });
    } else {
      nodes.forEach((node) => {
        if (!node) return;
        const nextPage = typeof node.page === 'number' ? node.page : pageNo;
        if (node.childElements) processNodeArray(node.childElements, nextPage);
        if (node.elements) processNodeArray(node.elements, nextPage);
        if (node.elementContainer) processNodeArray(node.elementContainer, nextPage);
      });
    }
  };

  processNodeArray(summaryPages);
  return sections;
};

// Collect form-level required fields, skipping:
//   - any field (or its entire subtree) whose ancestor container is hidden
//   - any field whose fieldId is a record-level key in an expandable table
const collectRequiredFields = (
  elements, recordFieldKeys = new Set(), formSubmissionPayload = {}
) => {
  const requiredFields = [];

  const traverse = (element, ancestorHidden = false) => {
    if (!element || typeof element !== 'object') return;

    // Propagate hidden state: if this element or any ancestor is hidden, skip entire subtree
    const thisHidden = ancestorHidden
      || evaluateIsHidden(element, formSubmissionPayload, null);
    if (thisHidden) return;

    const fieldLabel = typeof element.fieldLabel === 'object'
      ? element.fieldLabel?.label
      : element.fieldLabel;

    if (
      element.fieldRequired === true
      && element.fieldId
      && VALIDATABLE_INPUT_TYPES.has(element.fieldType)
      && !recordFieldKeys.has(element.fieldId)
    ) {
      requiredFields.push({
        fieldId: element.fieldId,
        label: fieldLabel || element.fieldId,
        element,
      });
    }

    getNestedElements(element).forEach((child) => traverse(child, false));
  };

  asArray(elements).forEach((element) => traverse(element, false));
  return requiredFields;
};

// Collect fields that carry a crossEntityValidation config — used to detect stale references
// (e.g. a Page-8 selector whose chosen value was a Page-6 record that has since been deleted).
// Skips hidden elements using the same ancestor-propagation logic as collectRequiredFields.
const collectCrossEntityFields = (elements, formSubmissionPayload = {}) => {
  const fields = [];
  const traverse = (element, ancestorHidden = false) => {
    if (!element || typeof element !== 'object') return;
    const thisHidden = ancestorHidden
      || evaluateIsHidden(element, formSubmissionPayload, null);
    if (thisHidden) return;
    if (
      element.fieldId
      && element.crossEntityValidation
      && typeof element.crossEntityValidation === 'object'
      && VALIDATABLE_INPUT_TYPES.has(element.fieldType)
    ) {
      const rawLabel = element.fieldLabel;
      const label = (typeof rawLabel === 'object' ? rawLabel?.label : rawLabel) || element.fieldId;
      fields.push({
        fieldId: element.fieldId,
        label,
        crossEntityValidation: element.crossEntityValidation,
      });
    }
    getNestedElements(element).forEach((child) => traverse(child, false));
  };
  asArray(elements).forEach((element) => traverse(element, false));
  return fields;
};

// Only Format 2 (multiKeyValueMappings + mapCondition "AND" on formSubmissionPayload keys) is
// trusted to skip a table subtree. Format 1 (propertyKey + mapCondition object) is frequently
// used elsewhere with self-referential "entityData.<table>.length" conditions that evaluateIsHidden
// cannot resolve (it only reads formSubmissionPayload/record, and doesn't support "greaterThan"),
// which would make it always evaluate as hidden and silently disable isTableLengthRequired checks
// on unrelated forms/pages. Restricting to Format 2 keeps this fix scoped to how the product-page
// checkbox-gated tables are actually authored, without risking a regression elsewhere.
const isHiddenForTableCollection = (element, formSubmissionPayload) => {
  const fieldDynamicDisplay = element?.fieldDynamicDisplay;
  if (!fieldDynamicDisplay || typeof fieldDynamicDisplay !== 'object') return false;
  if (fieldDynamicDisplay.multiKeyValueMappings && fieldDynamicDisplay.mapCondition === 'AND') {
    return evaluateIsHidden(element, formSubmissionPayload, null);
  }
  return false;
};

const collectExpandableTableConfigs = (elements, formSubmissionPayload = {}) => {
  const tableConfigs = [];

  const traverse = (element, ancestorHidden = false) => {
    if (!element || typeof element !== 'object') return;
    const thisHidden = ancestorHidden
      || isHiddenForTableCollection(element, formSubmissionPayload);
    if (thisHidden) return;
    if (element.fieldType === 'expandableTable' && element.showId) {
      tableConfigs.push(element);
    }

    getNestedElements(element).forEach((child) => traverse(child, false));
  };

  asArray(elements).forEach((element) => traverse(element, false));
  return tableConfigs;
};

// Collect elements carrying a checkboxGroupValidation config — used to enforce
// "at least N of these checkbox fields must be checked" rules, optionally scoped
// to only apply when a parent field is itself checked. Skips hidden subtrees.
// JSON config on the field element:
//   "checkboxGroupValidation": {
//   "parentField": "corporateCardSelection",  ← optional; applies when this field is truthy
//     "childFields": ["a", "b", "c"],           ← formSubmissionPayload keys to count
//     "requiredCount": 1,                        ← min. childFields count that must be truthy
//     "errorMessage": "Veuillez sélectionner au moins un produit."
//   }
const collectCheckboxGroupValidations = (elements, formSubmissionPayload = {}) => {
  const configs = [];
  const traverse = (element, ancestorHidden = false) => {
    if (!element || typeof element !== 'object') return;
    const thisHidden = ancestorHidden
      || evaluateIsHidden(element, formSubmissionPayload, null);
    if (thisHidden) return;
    if (element.checkboxGroupValidation && typeof element.checkboxGroupValidation === 'object') {
      configs.push(element.checkboxGroupValidation);
    }
    getNestedElements(element).forEach((child) => traverse(child, false));
  };
  asArray(elements).forEach((element) => traverse(element, false));
  return configs;
};

// Resolve the label for a verifyRequiredFields entry:
// prefer explicit label on the entry, then element fieldLabel, then fall back to key
const resolveFieldLabel = (verifyField, elementByFieldId) => {
  if (verifyField?.label) return verifyField.label;
  const element = elementByFieldId?.[verifyField?.key];
  if (element) {
    const rawLabel = element.fieldLabel;
    return (typeof rawLabel === 'object' ? rawLabel?.label : rawLabel) || verifyField.key;
  }
  return verifyField?.key || '';
};

const DEFAULT_VALIDATION_MESSAGES = {
  fieldRequired: '{label} is required.',
  recordStale: '{label}: selected record no longer exists, please re-select.',
  tableRequired: '{label}: at least one record is required.',
  recordFieldRequired: '{label} is required.',
};

const formatMsg = (template, replacements) => template.replace(/{(\w+)}/g, (_, key) => replacements[key] ?? '');

export const validateSummaryMandatoryFields = ({
  formElements,
  formSubmissionPayload,
  entityData,
  validationMessages,
}) => {
  const msgs = { ...DEFAULT_VALIDATION_MESSAGES, ...validationMessages };
  const sections = extractSummarySections(formElements);
  const sectionErrorsById = {};
  const recordErrorsById = {};
  const erroredRecordIdsBySectionId = {};

  sections.forEach((section) => {
    const sectionErrors = [];
    const expandableTableConfigs = collectExpandableTableConfigs(
      section.elements, formSubmissionPayload
    );
    const hasExpandableTables = expandableTableConfigs.length > 0;

    // Build element map once per section for label lookup and hidden-field checks
    const elementByFieldId = buildElementByFieldId(section.elements);

    // Only validate form-level required fields for sections WITHOUT expandable tables.
    // Sections with expandable tables store all their data in entityData records,
    // NOT in formSubmissionPayload — validate those via table record checks below.
    if (!hasExpandableTables) {
      // collectRequiredFields now handles hidden ancestor propagation internally
      const requiredFields = collectRequiredFields(
        section.elements, new Set(), formSubmissionPayload
      );
      requiredFields.forEach((field) => {
        if (isMissingValue(getPayloadValue(field.fieldId, formSubmissionPayload))) {
          sectionErrors.push(formatMsg(msgs.fieldRequired, { label: field.label }));
        }
      });
    }

    // Cross-entity reference validation: detect stale values that point to a deleted record.
    // Runs for ALL sections (with or without expandable tables) because the referencing field
    // is always a form-level payload field, not a record field.
    // JSON config on the field element:
    //   "crossEntityValidation": {
    //     "sourceEntityId": "signataires",   ← entityData key of the referenced table
    //     "matchKey": "compositeKey",         ← record property that equals the payload value
    //     "allowOtherValue": "other"           ← optional: when set, the value is always valid
    //   }
    const crossEntityFields = collectCrossEntityFields(section.elements, formSubmissionPayload);
    crossEntityFields.forEach((field) => {
      const value = getPayloadValue(field.fieldId, formSubmissionPayload);
      if (isMissingValue(value)) return; // missing-value already handled by requiredFields check
      const { sourceEntityId, matchKey, allowOtherValue } = field.crossEntityValidation;
      // When allowOtherValue is set and matches, the selection is always valid — skip stale check
      if (allowOtherValue !== undefined && value === allowOtherValue) return;
      const records = Array.isArray(entityData?.[sourceEntityId]) ? entityData[sourceEntityId] : [];
      const stillExists = records.some((record) => record?.[matchKey] === value);
      if (!stillExists) {
        sectionErrors.push(formatMsg(msgs.recordStale, { label: field.label }));
      }
    });

    // Checkbox group validation: "at least N of these checkboxes must be checked", optionally
    // scoped to a parent field. Runs for ALL sections (form-level payload fields only).
    const checkboxGroupConfigs = collectCheckboxGroupValidations(
      section.elements, formSubmissionPayload
    );
    checkboxGroupConfigs.forEach((config) => {
      const {
        parentField, childFields, requiredCount = 1, errorMessage,
      } = config;
      if (!Array.isArray(childFields) || childFields.length === 0 || !errorMessage) return;
      if (parentField && isMissingValue(
        getPayloadValue(parentField, formSubmissionPayload)
      )) return;
      const satisfiedCount = childFields.filter(
        (key) => !isMissingValue(getPayloadValue(key, formSubmissionPayload))
      ).length;
      if (satisfiedCount < requiredCount) {
        sectionErrors.push(errorMessage);
      }
    });

    // Group tables by dependencyGroupId for conditional group validation
    const tablesByGroupId = {};
    const ungroupedTables = [];

    expandableTableConfigs.forEach((tableConfig) => {
      const groupId = tableConfig?.dependencyGroupId;
      if (groupId) {
        if (!tablesByGroupId[groupId]) tablesByGroupId[groupId] = [];
        tablesByGroupId[groupId].push(tableConfig);
      } else {
        ungroupedTables.push(tableConfig);
      }
    });

    // Grouped tables: at least one table in the group must have records when condition is met
    Object.values(tablesByGroupId).forEach((groupTables) => {
      const groupRequirementMet = groupTables.some(
        (table) => isTableRequirementMet(table, formSubmissionPayload)
      );
      if (!groupRequirementMet) return;

      const hasAtLeastOneRecord = groupTables.some((tableConfig) => {
        const records = Array.isArray(entityData?.[tableConfig.showId])
          ? entityData[tableConfig.showId]
          : [];
        return records.length > 0;
      });

      if (!hasAtLeastOneRecord) {
        // Use section heading instead of table label
        sectionErrors.push(formatMsg(msgs.tableRequired, { label: section.label }));
      }
    });

    // Ungrouped tables: validate minimum record count and per-record required fields
    ungroupedTables.forEach((tableConfig) => {
      if (!isTableRequirementMet(tableConfig, formSubmissionPayload)) return;

      // Records live in entityData keyed by showId
      const records = Array.isArray(entityData?.[tableConfig.showId])
        ? entityData[tableConfig.showId]
        : [];

      if (tableConfig.isTableLengthRequired && records.length === 0) {
        // Prefer a table-specific label (needed when a section has multiple tables),
        // falling back to the section heading for backward compatibility.
        const tableLabel = tableConfig.recordRequiredLabel || section.label;
        sectionErrors.push(formatMsg(msgs.tableRequired, { label: tableLabel }));
        return;
      }

      const verifyFields = asArray(tableConfig.verifyRequiredFields).filter((f) => f?.key);

      if (verifyFields.length > 0 && records.length > 0) {
        records.forEach((record, rowIndex) => {
          const missingLabels = verifyFields
            .filter((verifyField) => {
              // Skip if field's condition is not met
              if (verifyField.condition) {
                const condVal = record?.[verifyField.condition.key];
                if (condVal !== verifyField.condition.value) return false;
              }
              // Skip if field is hidden by display conditions (check against record + payload)
              const element = elementByFieldId[verifyField.key];
              if (element && evaluateIsHidden(element, formSubmissionPayload, record)) {
                return false;
              }
              return isMissingValue(record?.[verifyField.key]);
            })
            .map((verifyField) => resolveFieldLabel(verifyField, elementByFieldId));

          if (missingLabels.length > 0) {
            const recordId = `${String(section.id)}-${tableConfig.showId}-record-${rowIndex}`;
            // Use *DisplayName key if present in record, otherwise fall back to "Record N"
            const displayNameKey = Object.keys(record).find(
              (k) => k.toLowerCase().includes('displayname')
            );
            const recordLabel = (displayNameKey && record[displayNameKey])
              || `Record ${rowIndex + 1}`;
            const errorMessages = missingLabels.map(
              (label) => formatMsg(msgs.recordFieldRequired, { label })
            );
            errorMessages.unshift(`${recordLabel}:`);
            recordErrorsById[recordId] = errorMessages;
            // Track sectionId → recordIds for child accordion auto-open in FormSummarySection
            const sId = String(section.id);
            if (!erroredRecordIdsBySectionId[sId]) erroredRecordIdsBySectionId[sId] = [];
            erroredRecordIdsBySectionId[sId].push(recordId);
          }
        });
      }
    });

    if (sectionErrors.length > 0) {
      sectionErrorsById[section.id] = [...new Set(sectionErrors)];
    }
  });

  const erroredSectionIds = Object.keys(sectionErrorsById);
  const erroredRecordIds = Object.keys(recordErrorsById);

  return {
    hasErrors: erroredSectionIds.length > 0 || erroredRecordIds.length > 0,
    sectionErrorsById,
    erroredSectionIds,
    recordErrorsById,
    erroredRecordIds,
    erroredRecordIdsBySectionId,
  };
};
